/* ---------------------------------------------------------------------------
   The network summary — the second document this application produces.

   The inventory sheet answers "what is this structure". This answers "what has
   this division got, and how much of it needs attention". It is the report a
   division engineer takes into a budget meeting, and the one thing the register
   could not produce.

   It is a DOCUMENT, not a dashboard, and it reuses the inventory sheet's own
   `.report` styling and print rules rather than inventing a second print path —
   which is the whole reason `GR-18` was scoped as minor. A masthead, numbered
   sections, hairline tables, a sign-off block, and it prints.

   WHAT IT REFUSES TO DO. Every figure here is a count of records, and the
   register is five records deep. So each section says what it could not
   determine as prominently as what it could: how many structures have never
   been inspected, how many carry an unreadable rating scale, how many could not
   be screened, how much of the network has no consequence data at all. A summary
   that reported only the structures it could place would make a register of five
   drawings look like a surveyed network.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { allBridges, districtNames, longDate, roadRollups } from '../data/derive.ts';

import { t } from '../data/locale.ts';
import { BANDS, bandLabel } from '../data/screening.ts';
import { sumRecorded } from '../data/recorded.ts';
import { agency } from '../tenant/agency.ts';
import {
  CONDITION_BANDS,
  complianceSummary,
  conditionSummary,
  groupBys,
  groupLabel,
  groupSummaries,
  type GroupBy,
  type GroupSummary,
} from '../data/summary.ts';

/** Percentage, or an em dash where there is nothing to take a percentage of. */
function pct(value: number, of: number): string {
  return of === 0 ? '—' : `${Math.round((value / of) * 100)}%`;
}

/**
 * A table with a header row and a totals row.
 *
 * The totals row is not optional and not a convenience: a summary broken into
 * eleven divisions whose columns do not add up to the network is a summary
 * somebody will quote one row of.
 */
function table(head: string[], rows: (string | number)[][], total: (string | number)[]): HTMLElement {
  return h(
    'table',
    { class: 'findings' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        ...head.map((label, i) =>
          h('th', { class: i === 0 ? undefined : 'r', text: label }),
        ),
      ),
    ),
    h(
      'tbody',
      {},
      ...rows.map((cells) =>
        h(
          'tr',
          {},
          ...cells.map((cell, i) =>
            h('td', {
              class: i === 0 ? undefined : 'r num',
              text: String(cell),
            }),
          ),
        ),
      ),
      h(
        'tr',
        { style: 'font-weight:600' },
        ...total.map((cell, i) =>
          h('td', {
            class: i === 0 ? undefined : 'r num',
            style: 'border-top:1.5px solid var(--stroke2)',
            text: String(cell),
          }),
        ),
      ),
    ),
  );
}

/** The control that chooses what the report is broken down by. */
function groupSwitch(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'sum__switch', role: 'group', 'aria-label': 'Group by' },
    h('span', { class: 'sum__switch-label', text: 'Grouped by' }),
    ...groupBys().map((by) => {
      const on = s.summaryBy === by;
      return h(
        'button',
        {
          type: 'button',
          class: ['nmap__btn', 'nmap__btn--base', on && 'nmap__btn--on'],
          'aria-pressed': on ? 'true' : 'false',
          onclick: () => actions.summaryGroupedBy(by),
        },
        t(groupLabel(by)),
      );
    }),
  );
}

/* --------------------------- sections --------------------------- */

function complianceTable(groups: GroupSummary[], by: GroupBy): HTMLElement {
  const all = groups.flatMap((g) => g.bridges);
  const total = complianceSummary(all);

  return table(
    [
      t(groupLabel(by)),
      t('Structures'),
      t('Routine'),
      t('Principal'),
      t('Never inspected'),
      t('Outstanding'),
      'Compliant',
    ],
    groups.map((g) => [
      g.label,
      g.bridges.length,
      g.compliance.routineOut,
      g.compliance.principalOut,
      g.compliance.neverInspected,
      g.compliance.outstanding,
      pct(g.bridges.length - g.compliance.outstanding, g.bridges.length),
    ]),
    [
      t('Total'),
      total.total,
      total.routineOut,
      total.principalOut,
      total.neverInspected,
      total.outstanding,
      pct(total.total - total.outstanding, total.total),
    ],
  );
}

function conditionTable(groups: GroupSummary[], by: GroupBy): HTMLElement {
  const all = groups.flatMap((g) => g.bridges);
  const total = conditionSummary(all);

  return table(
    [t(groupLabel(by)), ...CONDITION_BANDS.map((band) => t(band))],
    groups.map((g) => [g.label, ...CONDITION_BANDS.map((band) => g.condition.counts[band])]),
    [t('Total'), ...CONDITION_BANDS.map((band) => total.counts[band])],
  );
}

function screeningTable(groups: GroupSummary[], by: GroupBy): HTMLElement {
  const totals = BANDS.map((band) => groups.reduce((n, g) => n + g.screening[band], 0));

  return table(
    [t(groupLabel(by)), ...BANDS.map((band) => bandLabel(band))],
    groups.map((g) => [g.label, ...BANDS.map((band) => g.screening[band])]),
    [t('Total'), ...totals],
  );
}

/* --------------------------- the document --------------------------- */

export function summaryScreen(s: Readonly<State>): HTMLElement {
  const list = allBridges();
  const by = s.summaryBy;
  const groups = groupSummaries(list, by);

  const decks = sumRecorded(list, (b) => b.length);
  const totalLength = decks.total;
  const consequenceless = list.filter(
    (b) => b.aadt == null && b.detourKm == null && b.detourMinutes == null && b.lifeline == null,
  ).length;

  return h(
    'div',
    { class: 'report' },

    /* --- masthead: the inventory sheet's, so the two documents are visibly
       from the same office --- */
    h(
      'div',
      { class: 'report__masthead' },
      h(
        'div',
        {},
        h('div', {
          class: 'report__agency',
          /* Both through `t()`, and the sovereign from the agency block rather
             than hard-coded. The Nepali catalogue is keyed by the English
             string, so "Government of Nepal" still translates for DoR and a
             department with no catalogue entry falls back to its own English —
             which is exactly what `t()` is for. */
          text: `${t(agency().government)} · ${t(agency().ministry)}`,
        }),
        h('h2', { class: 'report__dept', text: `${t(agency().department)} — ${t(agency().branch)}` }),
        h('div', {
          style: 'font-size:12px;color:var(--txt2)',
          text: `${t('Bridge Network Summary')} · ${agency().unit}`,
        }),
      ),
      h(
        'div',
        { class: 'report__issue' },
        h('div', {}, `${t('Structures on file')} `, h('strong', { text: String(list.length) })),
        h('div', { text: `Issued ${longDate(new Date())}` }),
        h('div', {
          text: s.backend === 'live' ? 'Source: D1 register' : 'Source: bundled seed (offline)',
        }),
      ),
    ),

    h(
      'div',
      { class: 'report__head' },
      h(
        'div',
        { style: 'min-width:0' },
        h('div', { class: 'report__title', text: t('Network summary') }),
      ),
    ),

    groupSwitch(s),

    /* --- 1 --- */
    h('div', { class: 'report__h', text: `1 · ${t('Network summary')}` }),
    h(
      'div',
      { class: 'sum__figures' },
      ...[
        { label: t('Structures on file'), value: String(list.length) },
        { label: t('Total deck length'), value: `${totalLength.toFixed(1)} m` },
        { label: t('Districts'), value: String(districtNames(list).length) },
        { label: t('Roads'), value: String(roadRollups(list).length) },
      ].map((fig) =>
        h(
          'div',
          { class: 'sum__fig' },
          h('div', { class: 'sum__fig-v', text: fig.value }),
          h('div', { class: 'sum__fig-k', text: fig.label }),
        ),
      ),
    ),

    /* --- 2 --- */
    h('div', { class: 'report__h', text: `2 · ${t('Inspection compliance')}` }),
    complianceTable(groups, by),

    /* --- 3 --- */
    h('div', { class: 'report__h', text: `3 · ${t('Condition distribution')}` }),
    conditionTable(groups, by),

    /* --- 4 --- */
    h('div', { class: 'report__h', text: '4 · Flood, scour and seismic screening' }),
    screeningTable(groups, by),

    /* --- 5 --- */
    h('div', { class: 'report__h', text: '5 · Consequence of closure' }),
    h('div', {
      class: 'sum__note',
      style: 'margin-top:10px',
      text: `${list.length - consequenceless} of ${list.length} structures carry consequence data.`,
    }),

    /* --- sign-off, the inventory sheet's own --- */
    h(
      'div',
      { class: 'signoff' },
      h('div', { class: 'signoff__cap', text: `${t('Prepared by')} · Division Engineer` }),
      h('div', { class: 'signoff__cap', text: `${t('Checked by')} · Senior Divisional Engineer` }),
      h('div', { class: 'signoff__cap', text: `${t('Approved by')} · ${t(agency().branch)}` }),
    ),
  );
}
